import React, { FC, useCallback, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Link, generatePath } from 'react-router-dom'
import { toast } from 'react-toastify'
import clsx from 'clsx'

import { Grid, IconButton, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'

import { RedButton } from '../../../components/Buttons/RedButton'
import { InputSelect } from '../../../components/InputSelect'
import { GreenButton } from '../../../components/Buttons/GreenButton'

import { USER_TYPES } from '../../../constants/userTypes'
import { IData } from '../UserProfileTabs'
import { useRest } from '../../../components/Context'
import { routes } from '../../../constants/routes'
import { MemoChangeClientTypeOverride } from './ChangeClientTypeOverride'
import { icons } from '../../../constants/icons'
import { CommissionFeeUpdateModal } from '../UserProfileModals'
import { UserDetailsUpdateModal } from './UserDetailsUpdateModal'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    marginTop: theme.spacing(4),
    padding: theme.spacing(4),
  },
  field: {
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.primary.light,
    '&:not(:nth-last-of-type(-n+2))': { paddingBottom: theme.spacing(3) },
    '&>span:nth-child(1)': {
      opacity: '0.72',
      paddingRight: theme.spacing(1.5),
    },
    '&>span:nth-child(2)': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      opacity: 1,
    },
  },
  fieldBottom: {
    // '&:not(:last-of-type)': { paddingBottom: theme.spacing(1.8) },
  },
  buttonWrap: {
    justifyContent: 'flex-end',
  },
  introducerLink: {
    color: theme.palette.primary.light,
  },
  editIconGray: {
    color: '#a8a9ac',
    '&:hover': {
      color: theme.palette.primary.light,
    },
  },
}))

interface UserDetailsProps {
  data?: IData
  refreshProfile?: (value: boolean) => void
}

const formatDate = (value: string) => {
  const newDate = new Date(value)
  return newDate.toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

export const UserDetails: FC<UserDetailsProps> = ({ data, refreshProfile }) => {
  const classes = useStyles()
  const { rxiosGeneral } = useRest()
  const [isOpenClientDetailsModal, setIsOpenClientDetailsModal] = useState<boolean>(false)
  const [isOpenClientTypeOverride, setIsOpenClientTypeOverride] = useState<boolean>(false)
  const [isCommissionFeeModalOpen, setIsCommissionFeeModalOpen] = React.useState<boolean>(false)

  const formMethods = useForm({
    mode: 'onBlur',
  })

  const handleUpdateCommissionFeeModal = useCallback(() => {
    setIsCommissionFeeModalOpen((prev) => !prev)
  }, [])

  const handleRefreshProfile = useCallback(() => {
    if (refreshProfile) refreshProfile(true)
  }, [refreshProfile])

  const handleOpenClientDetailsModal = useCallback(() => {
    setIsOpenClientDetailsModal(true)
  }, [])

  const handleCloseClientDetailsModal = useCallback(() => {
    setIsOpenClientDetailsModal(false)
    handleRefreshProfile()
  }, [handleRefreshProfile])

  const toggleClientTypeOverrideModal = useCallback(() => {
    setIsOpenClientTypeOverride((prevState) => !prevState)
  }, [setIsOpenClientTypeOverride])

  const onSubmit = useCallback(() => {
    rxiosGeneral.post(`/user/password/`, { idSf: data?.idSf }).subscribe(
      // eslint-disable-next-line
      (next: any) => {},
      // eslint-disable-next-line
      (error: any) => {
        toast.error(error.response.data.message)
      },
      () => {
        toast.success('Password was reseted')
      },
    )
  }, [rxiosGeneral, data?.idSf])

  const formattedAssets = useMemo(() => {
    if (!data?.additionalAssets?.length) return '-'
    return data.additionalAssets
      .map((asset: { label: string }) => asset.label)
      .filter(Boolean)
      .join(', ')
  }, [data?.additionalAssets])

  return (
    <>
      <FormProvider {...formMethods}>
        <Grid container className={classes.container}>
          <Grid container item xs={10}>
            <Grid item xs={12} sm={6} className={classes.field}>
              <Typography component="span">First Name: </Typography>
              <Typography component="span">{data?.firstName || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={classes.field}>
              <Typography component="span">Last Name: </Typography>
              <Typography component="span">{data?.lastName || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={classes.field}>
              <Typography component="span">Email: </Typography>
              <Typography component="span">{data?.email || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={classes.field}>
              <Typography component="span">Date of Birth: </Typography>
              <Typography component="span">{data ? formatDate(data.birthday) : '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={classes.field}>
              <Typography component="span">User Name: </Typography>
              <Typography component="span">{data?.userName || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={classes.field}>
              <Typography component="span">Registered: </Typography>
              <Typography component="span">{data ? formatDate(data.createdAt) : '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={classes.field}>
              <Typography component="span">Country: </Typography>
              <Typography component="span">{data?.country || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={classes.field}>
              <Typography component="span">City: </Typography>
              <Typography component="span">{data?.city || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={classes.field}>
              <Typography component="span">Phone: </Typography>
              <Typography component="span">{data?.phone || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={classes.field}>
              <Typography component="span">Currency: </Typography>
              <Typography component="span">{data?.currency || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={classes.field}>
              <Typography component="span">Password: </Typography>
              <RedButton text="Reset Password" width="auto" onClick={onSubmit} />
            </Grid>
            {!!data?.introducedBy && (
              <Grid item xs={12} sm={6} className={classes.field}>
                <Typography component="span">Introduced By: </Typography>
                <Link
                  to={generatePath(routes.USER_DETAILS_TABS, { clientId: data.introducedBy.idSf })}
                  className={classes.introducerLink}
                >
                  <Typography component="span">{data.introducedBy.name || '-'}</Typography>
                </Link>
              </Grid>
            )}
          </Grid>
          <Grid container item xs={2} className={classes.buttonWrap}>
            <GreenButton text="Edit" width={120} onClick={handleOpenClientDetailsModal} />
          </Grid>
        </Grid>

        <Grid container className={classes.container}>
          <Grid container item xs={10}>
            <Grid item xs={12} sm={6} className={clsx(classes.field, classes.fieldBottom)}>
              <Typography component="span">Company: </Typography>
              <Typography component="span">{data?.companyName || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={clsx(classes.field, classes.fieldBottom)}>
              <Typography component="span">IBAN:</Typography>
              <Typography component="span">Coming soon..</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={clsx(classes.field, classes.fieldBottom)}>
              <Typography component="span">User Type: </Typography>
              {data?.role && (
                <InputSelect
                  width={200}
                  height={'unset'}
                  inputName="UserType"
                  enums={USER_TYPES}
                  defaultValue={data?.role}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} className={clsx(classes.field, classes.fieldBottom)}>
              <Typography component="span">BIC: </Typography>
              <Typography component="span">Coming soon..</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={clsx(classes.field, classes.fieldBottom)}>
              <Typography component="span">Commission rate: </Typography>
              <Typography component="span">{`${data?.commissionFee}%` || '-'}</Typography>
              <IconButton className={classes.editIconGray} onClick={handleUpdateCommissionFeeModal}>
                <span className="material-icons">{icons.EDIT}</span>
              </IconButton>
            </Grid>
          </Grid>
          <Grid container item xs={2} className={classes.buttonWrap}>
            <GreenButton text="Edit" width={120} />
          </Grid>
        </Grid>

        <Grid container className={classes.container}>
          <Grid container item xs={10}>
            <Grid item xs={12} sm={6} className={clsx(classes.field, classes.fieldBottom)}>
              <Typography component="span">Client Type: </Typography>
              <Typography component="span">{data?.clientType || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} className={clsx(classes.field, classes.fieldBottom)}>
              <Typography component="span">Groups Override:</Typography>
              <Typography component="span">{formattedAssets}</Typography>
            </Grid>
          </Grid>
          <Grid container item xs={2} className={classes.buttonWrap}>
            <GreenButton text="Edit" width={120} onClick={toggleClientTypeOverrideModal} />
          </Grid>
        </Grid>
      </FormProvider>

      <CommissionFeeUpdateModal
        isOpen={isCommissionFeeModalOpen}
        modalClose={handleUpdateCommissionFeeModal}
        idSf={data?.idSf}
        defaultValue={data?.commissionFee}
        refresh={handleRefreshProfile}
      />

      <UserDetailsUpdateModal
        isOpen={isOpenClientDetailsModal}
        modalClose={handleCloseClientDetailsModal}
        data={data}
        onSuccess={handleRefreshProfile}
      />

      <MemoChangeClientTypeOverride
        idSf={data?.idSf}
        isOpen={isOpenClientTypeOverride}
        modalClose={toggleClientTypeOverrideModal}
        clientTypeId={data?.clientTypeId}
        // eslint-disable-next-line
        userAssets={data?.additionalAssets}
      />
    </>
  )
}
